import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getMonitoringGroupUnprocessed } from '@/service/alerts'
import { reconnectManager } from '@/utils/reconnectManager'
import { extractMonitoringGroupListFromResponse } from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'

/** reconnectManager 注册 id，与 ServiceAlertsList 卸载时 unregister 成对 */
export const MONITORING_GROUP_UNPROCESSED_RECONNECT_ID = 'monitoring-group-unprocessed'

let monitoringGroupUnprocessedReconnectBound = false
let latestMonitoringGroupUnprocessedQuery = {}

/**
 * api.js 成功分支外会 `Promise.reject(响应体)`，catch 里拿到的是 { code, msg, data? }，没有 error.response
 * 网络错误时才是 Axios 风格带 response / message
 */
function normalizeApiCatchError (error) {
  if (error == null) return '加载监控分组预警失败'
  if (typeof error === 'string') return error
  // http 4xx/5xx，axios 仍可能带 response
  const d = error.response && error.response.data
  if (d != null) {
    if (typeof d === 'string') return d
    if (typeof d === 'object' && (d.msg != null || d.message != null)) {
      return String(d.msg != null && d.msg !== '' ? d.msg : d.message)
    }
  }
  // 业务失败：直接 reject 的 body
  if (typeof error === 'object' && (error.msg != null || error.message != null)) {
    const m = error.msg != null && error.msg !== '' ? error.msg : error.message
    if (m != null && m !== '') return String(m)
  }
  if (error.message) return String(error.message)
  try {
    return JSON.stringify(error)
  } catch (e) {
    return '加载监控分组预警失败'
  }
}

const initialState = {
  /** GET /alarm/monitoring/group_unprocessed 原始分组 */
  groups: [],
  query: {},
  loading: false,
  error: null,
  loaded: false
}

function normalizeFetchArg(arg) {
  if (!arg || typeof arg !== 'object') {
    return { force: false, query: {} }
  }
  const { force, query, ...rest } = arg
  const q = query && typeof query === 'object' && !Array.isArray(query)
    ? { ...query }
    : { ...rest }
  return {
    force: force === true,
    query: q
  }
}

function isSameQuery(a = {}, b = {}) {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => String(a[key]) === String(b[key]))
}

/**
 * 进入指挥中心/服务预警页时加载一次：按设备序列号+通道分组的未处理监控预警
 *
 * 去重策略：
 * - 多个页面（如 commandCenter/Page 与 ServiceAlertsList）在 once 中都注册同一 dispatch 时，
 *   已加载完成（loaded=true）或正在加载（loading=true）会通过 condition 直接跳过请求；
 * - 传入 `{ force: true }` 可强制重拉（如手动刷新按钮）。
 */
export const fetchMonitoringGroupUnprocessed = createAsyncThunk(
  'monitoringGroupUnprocessed/fetchMonitoringGroupUnprocessed',
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const { query } = normalizeFetchArg(arg)
      latestMonitoringGroupUnprocessedQuery = query
      const res = await getMonitoringGroupUnprocessed(query)
      /** Api 已对 Java 体解包一次后放在 res.data，此处再兼容多种包一层结构 */
      const list = extractMonitoringGroupListFromResponse(res)
      ensureMonitoringGroupUnprocessedReconnect(dispatch)
      reconnectManager.touch(MONITORING_GROUP_UNPROCESSED_RECONNECT_ID)
      return { groups: list, query }
    } catch (error) {
      return rejectWithValue(normalizeApiCatchError(error))
    }
  },
  {
    condition: (arg, { getState }) => {
      const s = getState()?.alerts?.monitoringGroupUnprocessed
      // 进行中去重：force 也不并发重复打 HTTP
      if (s?.loading) return false
      const { force, query } = normalizeFetchArg(arg)
      if (force) return true
      if (!s) return true
      if (s.loaded && isSameQuery(s.query || {}, query)) return false
      return true
    }
  }
)

function ensureMonitoringGroupUnprocessedReconnect (dispatch) {
  if (monitoringGroupUnprocessedReconnectBound) return
  monitoringGroupUnprocessedReconnectBound = true
  reconnectManager.register({
    id: MONITORING_GROUP_UNPROCESSED_RECONNECT_ID,
    type: 'api',
    heartbeatTimeoutMs: 180 * 1000,
    // 活跃时间除「HTTP 成功」外，由 sseSlice onActivity 同步 touch（与 SSE 同一条链路，避免长连正常仍被 watchdog 误拉 HTTP）
    // 与 SSE 区分：浏览器常误报 online，HTTP 补偿拉取勿每次 online 都 force，仅「数据已过期」时再拉
    reconnectOnOnline: false,
    refetchAfterJavaSseReconnect: true,
    reconnect: () => {
      dispatch(fetchMonitoringGroupUnprocessed({
        force: true,
        ...latestMonitoringGroupUnprocessedQuery
      }))
    },
  })
}

export function unregisterMonitoringGroupUnprocessedReconnect () {
  if (!monitoringGroupUnprocessedReconnectBound) return
  monitoringGroupUnprocessedReconnectBound = false
  reconnectManager.unregister(MONITORING_GROUP_UNPROCESSED_RECONNECT_ID)
}

const monitoringGroupUnprocessedSlice = createSlice({
  name: 'monitoringGroupUnprocessed',
  initialState,
  reducers: {
    clearMonitoringGroupUnprocessed: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonitoringGroupUnprocessed.pending, (state, action) => {
        const { query } = normalizeFetchArg(action.meta.arg)
        state.loading = true
        state.error = null
        state.query = query
      })
      .addCase(fetchMonitoringGroupUnprocessed.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.groups = action.payload?.groups || []
        state.query = action.payload?.query || {}
        state.loaded = true
      })
      .addCase(fetchMonitoringGroupUnprocessed.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.loaded = true
        state.groups = []
      })
  }
})

export const { clearMonitoringGroupUnprocessed } = monitoringGroupUnprocessedSlice.actions
export default monitoringGroupUnprocessedSlice.reducer
